import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, Search, ChevronDown } from 'lucide-react';
import { useAssetCategoryStore } from '@/stores/assetCategoryStore';
import useGlobalCustomerStore from '@/stores/globalCustomerStore';
import { clsx } from 'clsx';

interface CategoryPickerProps {
  selectedCategoryIds: string[];
  onChange: (categoryIds: string[]) => void;
  customerId?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
}

const CategoryPicker: React.FC<CategoryPickerProps> = ({
  selectedCategoryIds,
  onChange,
  customerId: propCustomerId,
  error,
  disabled = false,
  required = false,
  label = 'Post Format',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { selectedCustomer } = useGlobalCustomerStore();
  const { categories, loading: isLoading, loadCategories } = useAssetCategoryStore();

  // Use prop customerId if provided, otherwise fall back to global store
  const effectiveCustomerId = propCustomerId || selectedCustomer?.id;

  // Update dropdown position when opened or on scroll/resize
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updatePosition();

    // Update position on scroll or resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Load categories when picker opens
  useEffect(() => {
    if (isOpen && effectiveCustomerId) {
      loadCategories(String(effectiveCustomerId), 'Active');
    }
  }, [isOpen, effectiveCustomerId, loadCategories]);

  const filteredCategories = categories.filter(
    (category) =>
      category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (category.description && category.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const toggleCategory = (categoryId: string) => {
    if (selectedCategoryIds.includes(categoryId)) {
      onChange(selectedCategoryIds.filter((id) => id !== categoryId));
    } else {
      onChange([...selectedCategoryIds, categoryId]);
    }
  };

  const selectedCount = selectedCategoryIds.length;

  // Get selected category names for display
  const selectedCategoryNames = categories
    .filter((cat) => selectedCategoryIds.includes(cat.id))
    .map((cat) => cat.name);

  // Render dropdown in a portal to escape overflow containers
  const dropdownContent = isOpen && createPortal(
    <>
      {/* Click outside to close */}
      <div
        className="fixed inset-0 z-[9998]"
        onClick={() => setIsOpen(false)}
      />

      {/* Dropdown Menu */}
      <div
        className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
        }}
      >
        {/* Search Input */}
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search post formats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Category List */}
        <div className="overflow-y-auto max-h-48">
          {isLoading ? (
            <div className="p-4 text-center text-xs text-gray-500">Loading post formats...</div>
          ) : filteredCategories.length === 0 ? (
            <div className="p-4 text-center text-xs text-gray-500">
              {searchQuery ? 'No post formats match your search' : 'No post formats available'}
            </div>
          ) : (
            filteredCategories.map((category) => {
              const isSelected = selectedCategoryIds.includes(category.id);
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => toggleCategory(category.id)}
                  className={clsx(
                    'w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors',
                    isSelected && 'bg-blue-50'
                  )}
                >
                  {/* Checkbox */}
                  <div
                    className={clsx(
                      'h-4 w-4 rounded border flex items-center justify-center flex-shrink-0',
                      isSelected
                        ? 'bg-blue-600 border-blue-600'
                        : 'border-gray-300'
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>

                  {/* Category Info */}
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium text-xs text-gray-900 truncate">
                      {category.name}
                    </div>
                    {category.description && (
                      <div className="text-xs text-gray-500 truncate">{category.description}</div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </>,
    document.body
  );

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {/* Dropdown Button */}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={clsx(
            'w-full flex items-center justify-between px-2 py-1.5 border rounded-lg text-left transition-colors',
            disabled
              ? 'bg-gray-100 cursor-not-allowed'
              : 'bg-white hover:border-gray-400 cursor-pointer',
            error ? 'border-red-500' : 'border-gray-300'
          )}
        >
          <span className={clsx('text-xs truncate', selectedCount === 0 ? 'text-gray-500' : 'text-gray-900')}>
            {selectedCount === 0
              ? 'Select post formats...'
              : selectedCategoryNames.length <= 2
              ? selectedCategoryNames.join(', ')
              : `${selectedCategoryNames.slice(0, 2).join(', ')} +${selectedCount - 2} more`}
          </span>
          <ChevronDown
            className={clsx('h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ml-1', isOpen && 'rotate-180')}
          />
        </button>
      </div>

      {/* Portal-rendered dropdown */}
      {dropdownContent}

      {/* Error Message */}
      {error && <p className="text-xs text-red-500">{error}</p>}

      {/* Helper Text */}
      {required && selectedCategoryIds.length === 0 && !error && (
        <p className="text-xs text-gray-500">At least one post format is required</p>
      )}
    </div>
  );
};

export default CategoryPicker;
